import { Component, OnDestroy, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { finalize, Subscription } from 'rxjs';
import { DynamicFormComponent } from 'src/app/components/dynamic-form/dynamic-form.component';
import { IconModule } from 'src/app/icon/icon.module';
import { MaterialModule } from 'src/app/material.module';
import { AuthService } from 'src/app/services/auth.service';
import { ToastService } from 'src/app/services/toast.service';
import { PATTERN } from 'src/app/shared/constants';
import { DIRECTION, FormControlType } from 'src/app/shared/enums';
import { IFormConfig } from 'src/app/shared/interfaces/IForm';
import { Router } from '@angular/router';
import { AdminService } from 'src/app/services/admin.service';

@Component({
  selector: 'app-change-password',
  imports: [MaterialModule, IconModule, DynamicFormComponent],
  templateUrl: './change-password.component.html'
})
export class ChangePasswordComponent implements OnDestroy {
  @ViewChild(DynamicFormComponent) dynamicFormComponent: DynamicFormComponent;
  passwordSubscription = new Subscription();
  changePasswordForm: FormGroup;
  formConfig: IFormConfig;
  spinLoader = false;

  constructor(
    private formBuilder: FormBuilder,
    private translateService: TranslateService,
    private router: Router,
    private toastService: ToastService,
    private authService: AuthService,
    private adminService: AdminService) {
      this.initForm();
  }

  initForm() {
    this.changePasswordForm = this.formBuilder.nonNullable.group(
      {
        currentPassword: [null, [Validators.required]],
        newPassword: [null, [Validators.required, Validators.pattern(PATTERN.PASSWORD)]],
        confirmPassword: [null, [Validators.required, this.passwordMatchValidator]]
      }
    );

    this.passwordSubscription.add(
      this.changePasswordForm.get('newPassword')!.valueChanges.subscribe(() => {
        this.changePasswordForm.get('confirmPassword')!.updateValueAndValidity();
      })
    );

    this.formConfig = {
      id: 'changePasswordForm',
      styles: {
        bodyClass: 'p-0',
        footerClass: 'flex justify-end border-t border-[#e0e2ec] pt-4! px-0! pb-0!'
      },
      actions: [
        {
          text: 'save',
          type: 'submit',
          class: 'h-[36px]! text-[12px]! cursor-pointer!'
        }
      ],
      items: [
        {
          key: 'currentPassword',
          label: 'current password',
          layoutConfig: [{ column: 12 }],
          controlType: FormControlType.INPUT,
          labelDirection: DIRECTION.INSIDE,
          inputConfig: { isPassword: true },
          validator: [{ errorType: 'required', errorMessage: 'current password required' }]
        },
        {
          key: 'newPassword',
          label: 'new password',
          layoutConfig: [{ column: 12 }],
          inputConfig: { isPassword: true },
          controlType: FormControlType.INPUT,
          labelDirection: DIRECTION.INSIDE,
          itemClass: 'mt-2',
          validator: [
            { errorType: 'required', errorMessage: 'new password required' },
            { errorType: 'pattern', errorMessage: 'must include uppercase number and special' }
          ]
        },
        {
          key: 'confirmPassword',
          label: 'confirm password',
          layoutConfig: [{ column: 12 }],
          inputConfig: { isPassword: true },
          controlType: FormControlType.INPUT,
          labelDirection: DIRECTION.INSIDE,
          itemClass: 'mt-2 mb-2!',
          validator: [
            { errorType: 'required', errorMessage: 'confirm password required' },
            { errorType: 'passwordMismatch', errorMessage: 'passwords do not match' }
          ]
        }
      ]
    };
  }

  passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
    const newPassword = control.parent?.get('newPassword')?.value;
    if (control.value && newPassword !== control.value) {
      return { passwordMismatch: true };
    }
    return null;
  }

  get isAdmin () {
    return this.router.url.startsWith('/admin');
  }

  onSubmit = () => {
    if (this.changePasswordForm.invalid) return this.changePasswordForm.markAllAsTouched();
    this.spinLoader = true;
    const service = this.isAdmin ? this.adminService : this.authService;
    service.resetPassword(this.changePasswordForm.value)
      .pipe(finalize(() => (this.spinLoader = false)))
      .subscribe({
        next: () => {
          this.toastService.success(this.translateService.instant('update successfully'));
          this.resetForm();
        },
        error: ({ error }) => {
          this.dynamicFormComponent.ValidatorError = error?.errors ?? [];
        }
      });
  };

  resetForm() {
    this.dynamicFormComponent.resetForm();
  }

  ngOnDestroy() {
    this.passwordSubscription.unsubscribe();
  }
}
